const READ_SELECTOR = 'p, h1, h2, h3, h4, h5, h6, li, a, button, label, span, dd, dt, td, th, blockquote, figcaption';
const SKIP_SELECTOR = '.cya11y-menu, .cya11y-widget, .cya11y-overlay, input, textarea, [contenteditable="true"]';
const SELECTION_DEBOUNCE_MS = 300;
const SELECTION_MIN_CHARS = 3;

// speechSynthesis is a window-level service that, on Chrome especially, can
// keep speaking after a page reload — the new page's script runs while the old
// page's speech queue is still draining. Cancel any stale queue at module load,
// and again on beforeunload, so reloads/navigation always silence speech.
if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
    try {
        window.speechSynthesis.cancel();
    } catch {
        // ignore
    }
    window.addEventListener('beforeunload', () => {
        try {
            window.speechSynthesis.cancel();
        } catch {
            // ignore
        }
    });
}

let mouseupHandler: ((e: MouseEvent) => void) | null = null;
let touchendHandler: ((e: TouchEvent) => void) | null = null;
let keyupHandler: ((e: KeyboardEvent) => void) | null = null;
let clickHandler: ((e: MouseEvent) => void) | null = null;
let focusHandler: ((e: FocusEvent) => void) | null = null;

let lastReadEl: HTMLElement | null = null;

let outlinedEl: HTMLElement | null = null;
let outlinedOriginalOutline: string | null = null;
let outlinedOriginalOutlineOffset: string | null = null;
let isEnabled = false;

// Chrome cuts off speechSynthesis after ~15s on a single utterance.
// pause() + resume() every 10s defeats it.
let chromeKeepalive: number | null = null;
let totalChunks = 0;
let finishedChunks = 0;

let selectionDebounce: number | null = null;
let lastSelectionText = '';

function clearOutline() {
    if (outlinedEl) {
        if (outlinedOriginalOutline !== null) outlinedEl.style.outline = outlinedOriginalOutline;
        if (outlinedOriginalOutlineOffset !== null) outlinedEl.style.outlineOffset = outlinedOriginalOutlineOffset;
    }
    outlinedEl = null;
    outlinedOriginalOutline = null;
    outlinedOriginalOutlineOffset = null;
}

function applyOutline(el: HTMLElement) {
    clearOutline();
    outlinedOriginalOutline = el.style.outline;
    outlinedOriginalOutlineOffset = el.style.outlineOffset;
    el.style.outline = '2px solid #1d4ed8';
    el.style.outlineOffset = '2px';
    outlinedEl = el;
}

function findReadable(target: HTMLElement): HTMLElement | null {
    const semantic = target.closest(READ_SELECTOR) as HTMLElement | null;
    if (semantic) return semantic;
    let el: HTMLElement | null = target;
    while (el && el !== document.body) {
        const text = (el.innerText || '').trim();
        if (text.length > 0 && text.length < 5000) {
            const hasDirectText = Array.from(el.childNodes).some(
                (n) => n.nodeType === Node.TEXT_NODE && n.textContent && n.textContent.trim().length > 0
            );
            if (hasDirectText) return el;
        }
        el = el.parentElement;
    }
    return null;
}

// Walk up the DOM from a node looking for the nearest `lang="…"` attribute.
// Returns the language tag (just the primary subtag, e.g. "hi" from "hi-IN")
// or null. We stop at <html> — its lang is handled separately as page-level
// fallback.
function langFromElement(node: Node | null): string | null {
    if (!node) return null;
    let el = (node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement) as HTMLElement | null;
    while (el && el !== document.documentElement) {
        const lang = el.getAttribute && el.getAttribute('lang');
        if (lang && lang.trim()) return lang.toLowerCase().split('-')[0];
        el = el.parentElement;
    }
    return null;
}

// Detect the script of the text to figure out which language voice to use.
// Used when no element-level `lang` attribute is set. Many languages share a
// script (Latin → 50+ langs, Cyrillic → ~7, Arabic → ~6, Devanagari → ~5) so
// this is a coarse hint, not a precise language ID. We pick the most common
// language for each script.
//
// Ranges are written as \uXXXX escapes (NOT raw characters) — many of these
// scripts render as visually identical empty boxes in code editors that lack
// the matching font, which makes raw-character literals dangerous to read or
// edit.
function detectLangFromText(text: string): string | null {
    // Japanese first — its kanji overlap with CJK unified ideographs, but
    // hiragana/katakana ranges only ever mean Japanese.
    if (/[\u3041-\u309F\u30A0-\u30FF]/.test(text)) return 'ja'; // Hiragana / Katakana
    if (/[\uAC00-\uD7AF]/.test(text)) return 'ko';                // Korean Hangul
    if (/[\u4E00-\u9FFF]/.test(text)) return 'zh';                // CJK Unified Ideographs (Chinese)
    if (/[\u0900-\u097F]/.test(text)) return 'hi';                // Devanagari (Hindi, Marathi, Sanskrit, Nepali)
    if (/[\u0D00-\u0D7F]/.test(text)) return 'ml';                // Malayalam
    if (/[\u0B80-\u0BFF]/.test(text)) return 'ta';                // Tamil
    if (/[\u0C80-\u0CFF]/.test(text)) return 'kn';                // Kannada
    if (/[\u0C00-\u0C7F]/.test(text)) return 'te';                // Telugu
    if (/[\u0980-\u09FF]/.test(text)) return 'bn';                // Bengali
    if (/[\u0A00-\u0A7F]/.test(text)) return 'pa';                // Gurmukhi (Punjabi)
    if (/[\u0A80-\u0AFF]/.test(text)) return 'gu';                // Gujarati
    if (/[\u0B00-\u0B7F]/.test(text)) return 'or';                // Odia
    if (/[\u0D80-\u0DFF]/.test(text)) return 'si';                // Sinhala
    if (/[\u0E00-\u0E7F]/.test(text)) return 'th';                // Thai
    if (/[\u0E80-\u0EFF]/.test(text)) return 'lo';                // Lao
    if (/[\u1000-\u109F]/.test(text)) return 'my';                // Myanmar (Burmese)
    if (/[\u1780-\u17FF]/.test(text)) return 'km';                // Khmer (Cambodian)
    if (/[\u0F00-\u0FFF]/.test(text)) return 'bo';                // Tibetan
    if (/[\u1800-\u18AF]/.test(text)) return 'mn';                // Mongolian
    if (/[\u10A0-\u10FF]/.test(text)) return 'ka';                // Georgian
    if (/[\u0530-\u058F]/.test(text)) return 'hy';                // Armenian
    if (/[\u1200-\u137F]/.test(text)) return 'am';                // Ethiopic (Amharic)
    if (/[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/.test(text)) return 'ar'; // Arabic
    if (/[\u0590-\u05FF]/.test(text)) return 'he';                // Hebrew
    if (/[\u0400-\u04FF]/.test(text)) return 'ru';                // Cyrillic (Russian, Ukrainian, Bulgarian, …)
    if (/[\u0370-\u03FF]/.test(text)) return 'el';                // Greek
    return null;
}

// Pick a voice for the given text. Lookup chain:
//   1. anchor element's `lang=""` attribute (most authoritative)
//   2. Unicode script detection on the text (handles non-Latin content)
//   3. <html lang> (page-level fallback)
//   4. 'en' (last resort)
// Voice priority within the chosen language:
//   1. Google voice in that language (Chrome's high-quality network TTS)
//   2. Any voice in that language
//   3. Any Google voice (better than hard-coded OS default for foreign text)
//   4. Browser default
function pickVoiceForText(text: string, anchor: Node | null): { voice: SpeechSynthesisVoice | null; lang: string } {
    const elementLang = langFromElement(anchor);
    const scriptLang = elementLang ? null : detectLangFromText(text);
    const pageLang = (document.documentElement.lang || '').toLowerCase().split('-')[0];
    const targetLang = elementLang || scriptLang || pageLang || 'en';
    const voices = window.speechSynthesis.getVoices();
    if (!voices.length) return { voice: null, lang: targetLang };
    const voice =
        voices.find((v) => /google/i.test(v.name) && v.lang.toLowerCase().startsWith(targetLang)) ||
        voices.find((v) => v.lang.toLowerCase().startsWith(targetLang)) ||
        voices.find((v) => /google/i.test(v.name)) ||
        voices.find((v) => v.default) ||
        null;
    return { voice, lang: targetLang };
}

function chunkText(text: string): string[] {
    const matches = text.match(/[^.!?\n]+(?:[.!?]+|\n+|$)/g);
    if (!matches) return [text];
    const out: string[] = [];
    for (const raw of matches) {
        const trimmed = raw.trim();
        if (!trimmed) continue;
        if (trimmed.length <= 220) {
            out.push(trimmed);
        } else {
            for (let i = 0; i < trimmed.length; i += 200) {
                out.push(trimmed.slice(i, i + 200));
            }
        }
    }
    return out.length ? out : [text];
}

function startKeepalive() {
    if (chromeKeepalive !== null) return;
    chromeKeepalive = window.setInterval(() => {
        const synth = window.speechSynthesis;
        if (!synth.speaking && !synth.pending) {
            stopKeepalive();
            return;
        }
        synth.pause();
        synth.resume();
    }, 10000);
}

function stopKeepalive() {
    if (chromeKeepalive !== null) {
        clearInterval(chromeKeepalive);
        chromeKeepalive = null;
    }
}

function speakChunked(text: string, anchor: Node | null = null) {
    const synth = window.speechSynthesis;
    synth.cancel();
    stopKeepalive();
    const chunks = chunkText(text);
    totalChunks = chunks.length;
    finishedChunks = 0;
    const { voice, lang } = pickVoiceForText(text, anchor);
    chunks.forEach((chunk) => {
        const u = new SpeechSynthesisUtterance(chunk);
        u.lang = lang;
        u.rate = 1;
        if (voice) u.voice = voice;
        u.onend = () => {
            finishedChunks += 1;
            if (finishedChunks >= totalChunks) {
                stopKeepalive();
                clearOutline();
            }
        };
        u.onerror = () => {
            finishedChunks += 1;
            if (finishedChunks >= totalChunks) {
                stopKeepalive();
                clearOutline();
            }
        };
        synth.speak(u);
    });
    if (chunks.length > 0) startKeepalive();
}

function isInsideSkippedZone(node: Node | null): boolean {
    if (!node) return false;
    const el = (node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement) as HTMLElement | null;
    if (!el) return false;
    return !!el.closest(SKIP_SELECTOR);
}

function readSelectionDebounced() {
    if (selectionDebounce !== null) clearTimeout(selectionDebounce);
    selectionDebounce = window.setTimeout(() => {
        selectionDebounce = null;
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) return;
        if (isInsideSkippedZone(sel.anchorNode) || isInsideSkippedZone(sel.focusNode)) return;
        const text = sel.toString().trim();
        if (text.length < SELECTION_MIN_CHARS) return;
        if (text === lastSelectionText) return;
        lastSelectionText = text;
        clearOutline();
        speakChunked(text, sel.anchorNode);
    }, SELECTION_DEBOUNCE_MS);
}

function handleMouseup(_e: MouseEvent) {
    readSelectionDebounced();
}

function handleTouchend(_e: TouchEvent) {
    readSelectionDebounced();
}

function handleKeyup(e: KeyboardEvent) {
    const isCmdA = (e.ctrlKey || e.metaKey) && (e.key === 'a' || e.key === 'A');
    const isShiftSel = e.shiftKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'Home' || e.key === 'End');
    if (!isCmdA && !isShiftSel) return;
    readSelectionDebounced();
}

// Interactive/actionable elements. Reading is triggered by keyboard focus (below),
// which is non-destructive, so a click on these must NOT be hijacked — it keeps
// performing the site's own action (navigate, submit, toggle, …).
const INTERACTIVE_SELECTOR =
    'a[href], button, input, select, textarea, summary, label, ' +
    '[role="button"], [role="link"], [role="menuitem"], [role="menuitemcheckbox"], ' +
    '[role="menuitemradio"], [role="tab"], [role="checkbox"], [role="radio"], ' +
    '[role="switch"], [role="option"]';

// Read the nearest readable block at `target`, outline it, and skip if it's the
// element we just read (prevents a focus+click on the same element re-reading it).
function readReadableAt(target: HTMLElement) {
    const el = findReadable(target);
    if (!el || el === lastReadEl) return;
    // Prefer visible text; for icon-only controls fall back to the accessible
    // name (aria-label / title) so buttons and links are still announced.
    const text =
        (el.innerText || '').trim() ||
        (el.getAttribute('aria-label') || el.getAttribute('title') || '').trim();
    if (text.length < 1) return;
    lastReadEl = el;
    lastSelectionText = '';
    applyOutline(el);
    speakChunked(text, el);
}

// Focus-to-read: keyboard navigation reads the focused control/text immediately.
// Form fields and editable regions are skipped (via isInsideSkippedZone) so
// typing isn't read back.
function handleFocus(e: FocusEvent) {
    const target = e.target as HTMLElement | null;
    if (!target || isInsideSkippedZone(target)) return;
    readReadableAt(target);
}

// Click-to-read for touch / explicit taps: reads NON-interactive text only.
// Interactive elements are left alone so their click still performs its action;
// they are read via keyboard focus instead. Never calls preventDefault.
function handleClick(e: MouseEvent) {
    const target = e.target as HTMLElement | null;
    if (!target || isInsideSkippedZone(target)) return;
    if (target.closest(INTERACTIVE_SELECTOR)) return;

    // Ignore clicks that are part of a text selection — handled by the mouseup path.
    const sel = window.getSelection();
    if (sel && !sel.isCollapsed && sel.toString().trim().length >= SELECTION_MIN_CHARS) return;

    readReadableAt(target);
}

export default function pageRead(enable = false) {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;

    if (enable && !isEnabled) {
        mouseupHandler = handleMouseup;
        touchendHandler = handleTouchend;
        keyupHandler = handleKeyup;
        clickHandler = handleClick;
        focusHandler = handleFocus;
        document.addEventListener('mouseup', mouseupHandler);
        document.addEventListener('touchend', touchendHandler);
        document.addEventListener('keyup', keyupHandler);
        document.addEventListener('click', clickHandler);
        document.addEventListener('focusin', focusHandler);

        isEnabled = true;
    } else if (!enable && isEnabled) {
        if (mouseupHandler) document.removeEventListener('mouseup', mouseupHandler);
        if (touchendHandler) document.removeEventListener('touchend', touchendHandler);
        if (keyupHandler) document.removeEventListener('keyup', keyupHandler);
        if (clickHandler) document.removeEventListener('click', clickHandler);
        if (focusHandler) document.removeEventListener('focusin', focusHandler);
        mouseupHandler = null;
        touchendHandler = null;
        keyupHandler = null;
        clickHandler = null;
        focusHandler = null;

        if (selectionDebounce !== null) {
            clearTimeout(selectionDebounce);
            selectionDebounce = null;
        }
        lastReadEl = null;

        window.speechSynthesis.cancel();
        stopKeepalive();
        clearOutline();
        lastSelectionText = '';
        isEnabled = false;
    }
}
